import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";

const WD = ["S", "M", "T", "W", "T", "F", "S"];
const pad = (n) => String(n).padStart(2, "0");
const bneDay = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "Australia/Brisbane" }); } catch { return ""; } };
const bneTime = (iso) => { try { return new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit", timeZone: "Australia/Brisbane" }); } catch { return ""; } };
const nextKey = (key) => { const [y, m, d] = key.split("-").map(Number); const dt = new Date(Date.UTC(y, m - 1, d)); dt.setUTCDate(dt.getUTCDate() + 1); return dt.toISOString().slice(0, 10); };

function DayPopover({ dayJobs, avail, pinned }) {
  const dnc = avail?.dnc || [];
  const ok = avail?.contactable || [];
  return (
    <div
      data-day-popover
      className={`absolute z-[60] left-1/2 -translate-x-1/2 bottom-full mb-2 w-60 text-left ${pinned ? "block pointer-events-auto" : "hidden group-hover:block pointer-events-none"}`}
    >
      <div className="bg-white border border-slate-200 rounded-lg shadow-xl p-2.5 space-y-2">
        {dayJobs.length > 0 && <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{dayJobs.length} job{dayJobs.length > 1 ? "s" : ""}</p>}
        {dayJobs.slice(0, 8).map((j) => {
          const Inner = (
            <>
              <p className="text-xs font-semibold text-slate-800 leading-tight">{j.time ? `${j.time} · ` : ""}{j.title}</p>
              {j.pic && <p className="text-[11px] text-slate-500"><span className="font-medium text-lime-700">PIC</span> {j.pic}</p>}
              {j.assistants?.length > 0 && <p className="text-[11px] text-slate-400">Crew: {j.assistants.join(", ")}</p>}
            </>
          );
          return pinned ? (
            <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`cal-popover-job-${j.id}`}
              className="block border-t border-slate-100 pt-1.5 first:border-0 first:pt-0 rounded hover:bg-slate-50 -mx-1 px-1 py-0.5">{Inner}</Link>
          ) : (
            <div key={j.id} className="border-t border-slate-100 pt-1.5 first:border-0 first:pt-0">{Inner}</div>
          );
        })}
        {(dnc.length > 0 || ok.length > 0) && (
          <div className="border-t border-slate-100 pt-1.5 space-y-1">
            {ok.length > 0 && (
              <p className="text-[11px] text-amber-700 flex items-start gap-1.5"><span className="h-2 w-2 rounded-full bg-amber-500 mt-1 shrink-0" /><span><b>Unavailable (OK to contact):</b> {ok.join(", ")}</span></p>
            )}
            {dnc.length > 0 && (
              <p className="text-[11px] text-red-700 flex items-start gap-1.5"><span className="h-2 w-2 rounded-full bg-red-500 mt-1 shrink-0" /><span><b>Unavailable (do not contact):</b> {dnc.join(", ")}</span></p>
            )}
          </div>
        )}
      </div>
      <div className="w-2.5 h-2.5 bg-white border-r border-b border-slate-200 rotate-45 mx-auto -mt-1.5" />
    </div>
  );
}

function MiniMonth({ year, month, counts, jobsByDay, availByDay, todayKey, onPick, dayPopover, openDay, setOpenDay, setHoverDay }) {
  const first = new Date(year, month, 1);
  const lead = first.getDay();
  const days = new Date(year, month + 1, 0).getDate();
  const label = first.toLocaleDateString("en-AU", { month: "long", year: "numeric" });
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 h-full flex flex-col" data-testid={`cal-month-${year}-${month + 1}`}>
      <p className="font-heading font-semibold text-slate-900 text-center mb-3">{label}</p>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase text-slate-400 mb-1">
        {WD.map((w, i) => <div key={`${w}-${i}`}>{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`blank-${i}`} />;
          const key = `${year}-${pad(month + 1)}-${pad(d)}`;
          const n = counts[key] || 0;
          const isToday = key === todayKey;
          const dayJobs = jobsByDay?.[key] || [];
          const av = availByDay?.[key];
          const dncN = av?.dnc?.length || 0;
          const okN = av?.contactable?.length || 0;
          const hasContent = dayJobs.length > 0 || dncN > 0 || okN > 0;
          const pinned = dayPopover && openDay === key;
          const onClick = () => {
            if (!hasContent) return;
            if (dayPopover) setOpenDay((prev) => (prev === key ? null : key));
            else if (n) onPick(key);
          };
          return (
            <div key={key} data-cal-day className="relative group"
              onMouseEnter={() => { setHoverDay(key); if (dayPopover) setOpenDay((prev) => (prev && prev !== key ? null : prev)); }}
              onMouseLeave={() => setHoverDay(null)}>
              <button onClick={onClick} disabled={!hasContent} data-testid={`cal-day-${key}`}
                className={`relative w-full aspect-square rounded-lg flex items-center justify-center text-xs transition-colors ${isToday ? "ring-2 ring-lime-400" : ""} ${pinned ? "bg-lime-200" : ""} ${n ? "bg-lime-50 text-slate-900 font-semibold hover:bg-lime-100 cursor-pointer" : hasContent ? "text-slate-900 hover:bg-slate-50 cursor-pointer" : "text-slate-400"}`}>
                <span>{d}</span>
                {(n > 0 || dncN > 0 || okN > 0) && (
                  <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-0.5">
                    {dncN > 0 && <span className="h-3.5 min-w-3.5 px-0.5 rounded-full bg-red-500 text-white text-[8px] font-bold flex items-center justify-center shadow-sm" data-testid={`cal-dnc-${key}`}>{dncN}</span>}
                    {okN > 0 && <span className="h-3.5 min-w-3.5 px-0.5 rounded-full bg-amber-500 text-white text-[8px] font-bold flex items-center justify-center shadow-sm" data-testid={`cal-ok-${key}`}>{okN}</span>}
                    {n > 0 && <span className="h-3.5 min-w-3.5 px-0.5 rounded-full bg-lime-400 text-slate-900 text-[8px] font-bold flex items-center justify-center shadow-sm" data-testid={`cal-count-${key}`}>{n}</span>}
                  </span>
                )}
              </button>
              {hasContent && <DayPopover dayJobs={dayJobs} avail={av} pinned={pinned} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StaffFilterBar({ roster, hasUnassigned, activeStaff, toggleStaff, setActiveStaff }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="calendar-staff-filter">
      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mr-1">Filter calendar by crew</span>
      {roster.map((e) => {
        const on = activeStaff.includes(e.id);
        return (
          <button key={e.id} onClick={() => toggleStaff(e.id)} data-testid={`cal-staff-${e.id}`}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${on ? "border-transparent text-white" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
            style={on ? { background: e.color } : undefined}>
            <span className="h-2 w-2 rounded-full" style={{ background: on ? "rgba(255,255,255,.9)" : e.color }} />
            {e.name}
          </button>
        );
      })}
      {hasUnassigned && (
        <button onClick={() => toggleStaff("__unassigned__")} data-testid="cal-staff-unassigned"
          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${activeStaff.includes("__unassigned__") ? "bg-slate-700 border-slate-700 text-white" : "bg-white border-dashed border-slate-300 text-slate-500 hover:border-slate-400"}`}>
          <span className="h-2 w-2 rounded-full bg-slate-400" /> Unassigned
        </button>
      )}
      {activeStaff.length > 0 && (
        <button onClick={() => setActiveStaff([])} data-testid="cal-staff-clear"
          className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2 ml-1">Show all</button>
      )}
    </div>
  );
}

export default function JobsCalendar({ jobs = [], todayKey, count = 3, extra = null, onPickDay, cols = "lg:grid-cols-3", stretch = false, empMap = {}, dayPopover = false, staffFilter = false, filterTop = false, availability = [], startYear = null, startMonth = null }) {
  const navigate = useNavigate();
  const [openDay, setOpenDay] = useState(null);
  const [, setHoverDay] = useState(null);
  const [activeStaff, setActiveStaff] = useState([]);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!dayPopover) return;
    const onDown = (e) => {
      if (!wrapRef.current) return;
      const t = e.target;
      if (t.closest("[data-cal-day]") || t.closest("[data-day-popover]")) return;
      setOpenDay(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("touchstart", onDown); };
  }, [dayPopover]);

  const activeJobs = jobs.filter((j) => j.status !== "cancelled");

  const rosterIds = [];
  activeJobs.forEach((j) => { [j.employee_id, ...(j.assistant_ids || [])].forEach((id) => { if (id && !rosterIds.includes(id)) rosterIds.push(id); }); });
  (availability || []).forEach((a) => { if (a.employee_id && !rosterIds.includes(a.employee_id)) rosterIds.push(a.employee_id); });
  const roster = rosterIds.map((id) => empMap[id]).filter(Boolean);
  const hasUnassigned = activeJobs.some((j) => !j.employee_id);
  const staffSel = activeStaff.filter((x) => x !== "__unassigned__");

  const shown = activeStaff.length
    ? activeJobs.filter((j) =>
        activeStaff.includes(j.employee_id) ||
        (j.assistant_ids || []).some((id) => activeStaff.includes(id)) ||
        (activeStaff.includes("__unassigned__") && !j.employee_id))
    : activeJobs;

  const counts = {};
  const jobsByDay = {};
  shown.forEach((j) => {
    const k = bneDay(j.start); if (!k) return;
    counts[k] = (counts[k] || 0) + 1;
    const pic = empMap?.[j.employee_id]?.name;
    const assistants = (j.assistant_ids || []).map((id) => empMap?.[id]?.name).filter(Boolean);
    (jobsByDay[k] = jobsByDay[k] || []).push({ id: j.id, title: j.title, time: bneTime(j.start), pic, assistants });
  });
  Object.values(jobsByDay).forEach((arr) => arr.sort((a, b) => (a.time || "").localeCompare(b.time || "")));

  // Availability per day (filtered by selected crew), expanded across date ranges.
  const availByDay = {};
  (availability || []).forEach((a) => {
    if (staffSel.length && !staffSel.includes(a.employee_id)) return;
    const name = empMap?.[a.employee_id]?.name || "Crew";
    const bucket = a.type === "dnc" ? "dnc" : "contactable";
    let d = a.start; const end = a.end || a.start; let guard = 0;
    while (d <= end && guard < 400) {
      const rec = availByDay[d] = availByDay[d] || { dnc: [], contactable: [] };
      if (!rec[bucket].includes(name)) rec[bucket].push(name);
      d = nextKey(d); guard += 1;
    }
  });

  const base0 = (startYear != null && startMonth != null) ? new Date(startYear, startMonth, 1) : new Date();
  const months = Array.from({ length: count }).map((_, o) => { const b = new Date(base0.getFullYear(), base0.getMonth() + o, 1); return { year: b.getFullYear(), month: b.getMonth() }; });
  const pick = onPickDay || (() => navigate("/calendar"));
  const toggleStaff = (id) => setActiveStaff((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const filterBar = staffFilter && (roster.length > 0 || hasUnassigned) ? (
    <StaffFilterBar roster={roster} hasUnassigned={hasUnassigned} activeStaff={activeStaff} toggleStaff={toggleStaff} setActiveStaff={setActiveStaff} />
  ) : null;

  return (
    <div ref={wrapRef} className="space-y-4" data-testid="jobs-calendar-wrap">
      {filterTop && filterBar}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${cols} gap-4 ${stretch ? "items-stretch" : "items-start"}`} data-testid="jobs-calendar">
        {months.map((m) => (
          <MiniMonth key={`${m.year}-${m.month}`} {...m} counts={counts} jobsByDay={jobsByDay} availByDay={availByDay} todayKey={todayKey}
            onPick={pick} dayPopover={dayPopover} openDay={openDay} setOpenDay={setOpenDay} setHoverDay={setHoverDay} />
        ))}
        {extra}
      </div>
      {!filterTop && filterBar}
    </div>
  );
}
